import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Loader2, AlertCircle, Check, Clock, Users, Bell, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminScheduledAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    display_type: 'banner',
    scheduled_at: '',
    end_at: '',
    target_audience: 'all',
    is_active: true
  });

  const displayTypes = [
    { value: 'banner', label: 'Top Banner' },
    { value: 'popup', label: 'Popup Modal' },
    { value: 'toast', label: 'Toast Notification' }
  ];

  const targetAudiences = [
    { value: 'all', label: 'All Users' },
    { value: 'premium', label: 'Premium Only' },
    { value: 'free', label: 'Free Users Only' },
    { value: 'verified', label: 'Verified Users' },
    { value: 'unverified', label: 'Unverified Users' }
  ];

  const fetchAnnouncements = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/phase2/scheduled-announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data.announcements || []);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Failed to load scheduled announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleCreate = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content || !newAnnouncement.scheduled_at) {
      setError('Title, content, and scheduled time are required');
      return;
    }

    const token = localStorage.getItem('token');
    setSaving(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/admin/phase2/scheduled-announcements`, newAnnouncement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Announcement scheduled successfully');
      setTimeout(() => setSuccess(null), 3000);
      setShowModal(false);
      setNewAnnouncement({
        title: '',
        content: '',
        display_type: 'banner',
        scheduled_at: '',
        end_at: '',
        target_audience: 'all',
        is_active: true
      });
      fetchAnnouncements();
    } catch (err) {
      console.error('Error creating announcement:', err);
      setError(err.response?.data?.detail || 'Failed to schedule announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    const token = localStorage.getItem('token');
    setDeleting(announcementId);
    setError(null);

    try {
      await axios.delete(`${API_URL}/api/admin/phase2/scheduled-announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Announcement deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError(err.response?.data?.detail || 'Failed to delete announcement');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (announcement) => {
    const now = new Date();
    const scheduledAt = new Date(announcement.scheduled_at);
    const endAt = announcement.end_at ? new Date(announcement.end_at) : null;

    if (!announcement.is_active) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Inactive</span>;
    }
    if (endAt && now > endAt) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Expired</span>;
    }
    if (now >= scheduledAt) {
      return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Live</span>;
    }
    return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Scheduled</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-scheduled-announcements">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduled Announcements</h1>
          <p className="text-gray-600 mt-1">Schedule announcements to display at specific times</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors"
          data-testid="schedule-announcement-btn"
        >
          <Plus className="w-4 h-4" />
          Schedule Announcement
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {announcements.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No scheduled announcements</h3>
            <p className="text-gray-500 mb-4">Create your first scheduled announcement</p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Schedule Announcement
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="p-4 hover:bg-gray-50 transition-colors"
                data-testid={`announcement-${announcement.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-gray-900 truncate">{announcement.title}</h4>
                      {getStatusBadge(announcement)}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{announcement.content}</p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(announcement.scheduled_at)}
                      </span>
                      {announcement.end_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          Until: {formatDate(announcement.end_at)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Bell className="w-3.5 h-3.5" />
                        {displayTypes.find(t => t.value === announcement.display_type)?.label || announcement.display_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {targetAudiences.find(t => t.value === announcement.target_audience)?.label || announcement.target_audience}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDelete(announcement.id)}
                    disabled={deleting === announcement.id}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete announcement"
                  >
                    {deleting === announcement.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Schedule New Announcement</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Weekend Special Offer!"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  data-testid="input-title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                <textarea
                  value={newAnnouncement.content}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter the announcement message..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  data-testid="input-content"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Type</label>
                  <select
                    value={newAnnouncement.display_type}
                    onChange={(e) => setNewAnnouncement(prev => ({ ...prev, display_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  >
                    {displayTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                  <select
                    value={newAnnouncement.target_audience}
                    onChange={(e) => setNewAnnouncement(prev => ({ ...prev, target_audience: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  >
                    {targetAudiences.map(audience => (
                      <option key={audience.value} value={audience.value}>{audience.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={newAnnouncement.scheduled_at}
                    onChange={(e) => setNewAnnouncement(prev => ({ ...prev, scheduled_at: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    data-testid="input-scheduled-at"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time (Optional)</label>
                  <input
                    type="datetime-local"
                    value={newAnnouncement.end_at}
                    onChange={(e) => setNewAnnouncement(prev => ({ ...prev, end_at: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    data-testid="input-end-at"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newAnnouncement.is_active}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-mint-500 focus:ring-mint-500"
                />
                <span className="text-sm text-gray-700">Active immediately when scheduled time arrives</span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 disabled:opacity-50 transition-colors"
                data-testid="save-announcement-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4" />
                )}
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminScheduledAnnouncements;
